import React, { useState } from 'react';
import { 
  Phone, MessageSquare, MapPin, Clock, 
  Navigation, CheckCircle, ExternalLink, Calendar, CreditCard, 
  Copy, Check, Compass, Car, Mail
} from 'lucide-react';
import { salonInfo } from '../data/salonInfo';
import { useLanguage } from '../context/LanguageContext';

export default function Contact({ onOpenBooking }) {
  const { lang, t } = useLanguage();
  const [copiedAddress, setCopiedAddress] = useState(false);

  const directNavigationUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent('Aura Hair Samuel Esmeijerplein 3 3067 HP Rotterdam')}&destination_place_id=ChIJ5wrnPtraxUcR-dlmnTme7qc`;
  const appleMapsUrl = `https://maps.apple.com/?daddr=Samuel+Esmeijerplein+3,+3067+HP+Rotterdam,+Netherlands`;
  const wazeUrl = `https://waze.com/ul?ll=51.9408834,4.5486023&navigate=yes`;

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(salonInfo.address.full);
    setCopiedAddress(true);
    setTimeout(() => setCopiedAddress(false), 2500);
  };

  const cT = t.contact;

  return (
    <section id="iletisim" className="py-24 relative bg-white border-t border-slate-200 overflow-hidden w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-bold uppercase tracking-wider mb-3 shadow-sm">
            <MapPin className="w-3.5 h-3.5 text-amber-600" />
            <span>Samuel Esmeijerplein 3, Rotterdam</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            {cT.title1} <span className="gold-gradient-text">{cT.title2}</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-600">
            {cT.subtitle}
          </p>
        </div>

        {/* TOP: Interactive Live Map & Navigation Hub */}
        <div className="mb-12 rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            
            {/* Live Interactive Google Map Embed (7 Cols) */}
            <div className="lg:col-span-7 h-80 lg:h-auto min-h-[340px] relative bg-slate-100">
              <iframe
                title="Aura Hair Rotterdam Google Maps Locatie"
                src={`https://maps.google.com/maps?q=51.9408834,4.5486023&hl=${lang}&z=17&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full min-h-[340px]"
              />
              <div className="absolute top-3 left-3 px-3 py-1.5 rounded-xl bg-white/95 backdrop-blur-md border border-slate-200 text-xs font-bold text-slate-900 shadow-md flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span>Aura Hair &bull; Rotterdam</span>
              </div>
            </div>

            {/* Direct Directions & Navigation Box (5 Cols) */}
            <div className="lg:col-span-5 p-6 sm:p-8 flex flex-col justify-between space-y-6 bg-slate-50">
              <div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wider mb-2">
                  <Compass className="w-3.5 h-3.5 text-amber-600" />
                  <span>{cT.directNav}</span>
                </div>
                
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
                  Samuel Esmeijerplein 3
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
                  {cT.rotterdamDistrict}
                </p>

                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={handleCopyAddress}
                    className="px-3.5 py-2 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-xs font-bold text-slate-700 flex items-center gap-1.5 transition-colors shadow-sm"
                  >
                    {copiedAddress ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
                    <span>{copiedAddress ? cT.copied : cT.copyAddress}</span>
                  </button>
                </div>
              </div>

              {/* Navigation Action Buttons */}
              <div className="space-y-2.5 pt-4 border-t border-slate-200">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">
                  {cT.openInApp}
                </span>
                
                {/* Main Google Maps Button */}
                <a
                  href={directNavigationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-white font-bold text-xs uppercase tracking-wider shadow-md shadow-amber-500/25 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2.5"
                >
                  <Navigation className="w-4 h-4 fill-white" />
                  <span>{cT.getDirections}</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>

                {/* Alternative Maps Grid */}
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={appleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2.5 px-3 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-800 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors shadow-sm text-center"
                  >
                    <Car className="w-3.5 h-3.5 text-amber-600" />
                    <span>Apple Maps</span>
                  </a>
                  <a
                    href={wazeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2.5 px-3 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-800 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors shadow-sm text-center"
                  >
                    <Compass className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Waze Navigation</span>
                  </a>
                </div>
              </div>

            </div>

          </div>
        </div>

        {/* BOTTOM: Contact Details & Working Hours */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Col: Contact Info & Quick Actions (7 Cols) */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              
              {/* Phone Card */}
              <div className="p-5 rounded-2xl bg-white border border-slate-200 hover:border-amber-400 shadow-md hover:shadow-lg transition-all">
                <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700 mb-3">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="text-xs uppercase font-bold text-slate-600 tracking-wider">{cT.phoneTitle}</div>
                <p className="text-base font-extrabold text-slate-900 mt-1">{salonInfo.phone}</p>
                <a
                  href={`tel:${salonInfo.phoneRaw}`}
                  className="inline-flex items-center gap-1.5 text-xs text-amber-700 font-bold mt-3 hover:underline"
                >
                  <span>{cT.callNow}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              {/* Email / Online Inquiry Card */}
              <div className="p-5 rounded-2xl bg-white border border-slate-200 hover:border-amber-400 shadow-md hover:shadow-lg transition-all">
                <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700 mb-3">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="text-xs uppercase font-bold text-slate-600 tracking-wider">{lang === 'nl' ? 'E-mail & Vragen' : lang === 'tr' ? 'E-Posta & İletişim' : 'Email & Inquiries'}</div>
                <p className="text-base font-extrabold text-slate-900 mt-1">{salonInfo.email}</p>
                <a
                  href={`mailto:${salonInfo.email}`}
                  className="inline-flex items-center gap-1.5 text-xs text-amber-700 font-bold mt-3 hover:underline"
                >
                  <span>{lang === 'nl' ? 'E-mail Versturen' : lang === 'tr' ? 'E-Posta Gönder' : 'Send Email'}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

            </div>

            {/* Payment Methods Pill */}
            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-700 font-bold">
                <CreditCard className="w-4 h-4 text-amber-600" />
                <span>{cT.paymentMethodsTitle}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap text-[11px]">
                <span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 font-bold border border-emerald-200">PIN / Maestro</span>
                <span className="px-2.5 py-1 rounded-md bg-blue-50 text-blue-800 font-bold border border-blue-200">iDEAL</span>
                <span className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-800 font-semibold border border-slate-200">Apple Pay</span>
                <span className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-800 font-semibold border border-slate-200">
                  {lang === 'nl' ? 'Contant' : lang === 'tr' ? 'Nakit' : 'Cash'}
                </span>
              </div>
            </div>

            {/* Big CTA Banner */}
            <div className="p-6 rounded-3xl bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-serif text-xl font-bold text-white">
                  {cT.ctaTitle}
                </h3>
                <p className="text-xs text-amber-100 mt-0.5 font-medium">
                  {cT.ctaSubtitle}
                </p>
              </div>
              <button
                onClick={onOpenBooking}
                className="w-full sm:w-auto min-h-[44px] px-6 py-3 rounded-xl bg-white text-slate-900 hover:bg-amber-50 font-bold text-xs tracking-wider uppercase shadow-md transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer"
              >
                <Calendar className="w-4 h-4 text-amber-600" />
                <span>{t.nav.bookNow}</span>
              </button>
            </div>

          </div>

          {/* Right Col: Working Hours Schedule (5 Cols) */}
          <div className="lg:col-span-5">
            <div className="p-6 sm:p-7 rounded-3xl bg-white border border-slate-200 shadow-xl">
              <div className="flex items-center gap-3 pb-5 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-slate-900">{cT.workingHoursTitle}</h3>
                  <p className="text-xs text-slate-500 font-medium">Aura Hair Rotterdam</p>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                {salonInfo.workingHours.map((item, idx) => {
                  const dayName = typeof item.day === 'object' ? (item.day[lang] || item.day.nl) : item.day;
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs sm:text-sm"
                    >
                      <span className="font-bold text-slate-700">{dayName}</span>
                      <span className={`font-mono font-bold ${item.isOpen ? 'text-amber-700' : 'text-slate-400'}`}>
                        {item.isOpen ? item.hours : cT.closedText}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{cT.walkInNote}</span>
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
